"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useDebounce } from "react-use";
import type { ApiCreateGroupDescription } from "@/generated/models/ApiCreateGroupDescription";
import { useXtdhGrantQuery } from "@/hooks/useXtdhGrantQuery";
import GroupCreateXtdhGrantModal from "./GroupCreateXtdhGrantModal";
import GroupCreateXtdhGrantRow from "./subcomponents/GroupCreateXtdhGrantRow";
import { isSelectableNonGrantedStatus, toShortGrantId } from "./utils";
interface GroupCreateXtdhGrantProps {
  readonly beneficiaryGrantId: ApiCreateGroupDescription["is_beneficiary_of_grant_id"];
  readonly setBeneficiaryGrantId: (grantId: ApiCreateGroupDescription["is_beneficiary_of_grant_id"]) => void;
}
export default function GroupCreateXtdhGrant(t0) {
  const $ = _c(58);
  const {
    beneficiaryGrantId,
    setBeneficiaryGrantId
  } = t0;
  let t1;
  if ($[0] !== beneficiaryGrantId) {
    t1 = beneficiaryGrantId?.trim() ?? "";
    $[0] = beneficiaryGrantId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedGrantId = t1;
  const hasSelectedGrant = normalizedGrantId.length > 0;
  const [lookupGrantId, setLookupGrantId] = useState(hasSelectedGrant ? normalizedGrantId : null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t2;
  if ($[2] !== hasSelectedGrant || $[3] !== normalizedGrantId) {
    t2 = () => {
      setLookupGrantId(hasSelectedGrant ? normalizedGrantId : null);
    };
    $[2] = hasSelectedGrant;
    $[3] = normalizedGrantId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hasSelectedGrant || $[6] !== normalizedGrantId) {
    t3 = [hasSelectedGrant, normalizedGrantId];
    $[5] = hasSelectedGrant;
    $[6] = normalizedGrantId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useDebounce(t2, 250, t3);
  const t4 = !!lookupGrantId;
  let t5;
  if ($[8] !== lookupGrantId || $[9] !== t4) {
    t5 = {
      grantId: lookupGrantId,
      enabled: t4
    };
    $[8] = lookupGrantId;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    grant,
    isFetching,
    isError,
    errorMessage
  } = useXtdhGrantQuery(t5);
  const isLookupFresh = lookupGrantId === normalizedGrantId;
  let t6;
  if ($[11] !== grant || $[12] !== isLookupFresh) {
    t6 = isLookupFresh && grant?.status !== undefined && isSelectableNonGrantedStatus(grant.status);
    $[11] = grant;
    $[12] = isLookupFresh;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const showNonGrantedWarning = t6;
  const showLookupError = isLookupFresh && Boolean(lookupGrantId && isError);
  let t7;
  if ($[14] !== setBeneficiaryGrantId) {
    t7 = nextValue => {
      const normalized = nextValue.trim();
      setBeneficiaryGrantId(normalized.length ? normalized : null);
    };
    $[14] = setBeneficiaryGrantId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const onInputChange = t7;
  let t8;
  if ($[16] !== setBeneficiaryGrantId) {
    t8 = () => {
      setLookupGrantId(null);
      setBeneficiaryGrantId(null);
    };
    $[16] = setBeneficiaryGrantId;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const onClearSelection = t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50 sm:tw-text-lg">xTDH Grant Beneficiary</p><p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-iron-400">Optionally require identities to be beneficiaries of a selected xTDH grant.</p></div>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-400">Grant ID</span>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== onInputChange) {
    t11 = event => onInputChange(event.target.value);
    $[20] = onInputChange;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== normalizedGrantId || $[23] !== t11) {
    t12 = <label className="tw-block">{t10}<input type="text" value={normalizedGrantId} onChange={t11} placeholder="Paste grant id" className="tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-py-2.5 tw-text-sm tw-text-iron-50 tw-ring-1 tw-ring-inset tw-ring-iron-700 placeholder:tw-text-iron-500 focus:tw-ring-primary-400" /></label>;
    $[22] = normalizedGrantId;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <button type="button" onClick={() => setIsModalOpen(true)} className="tw-h-[42px] tw-self-end tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 desktop-hover:hover:tw-bg-iron-800">Find grant</button>;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = !normalizedGrantId.length;
  let t15;
  if ($[26] !== onClearSelection || $[27] !== t14) {
    t15 = <button type="button" onClick={onClearSelection} disabled={t14} className="tw-h-[42px] tw-self-end tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-text-sm tw-font-semibold tw-text-iron-300 tw-transition tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-bg-iron-800">Clear</button>;
    $[26] = onClearSelection;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t15) {
    t16 = <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-[minmax(0,1fr)_auto_auto]">{t12}{t13}{t15}</div>;
    $[29] = t12;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== isFetching || $[33] !== lookupGrantId) {
    t17 = isFetching && !!lookupGrantId && <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-font-medium tw-text-iron-400">Validating grant...</p>;
    $[32] = isFetching;
    $[33] = lookupGrantId;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== errorMessage || $[36] !== lookupGrantId || $[37] !== showLookupError) {
    t18 = showLookupError && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-red/30 tw-bg-red/10 tw-p-3"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-red">{errorMessage ?? "Unable to resolve grant ID."}</p><p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-red/90">The ID will still be submitted as entered:{" "}<span className="tw-font-semibold">{toShortGrantId(lookupGrantId)}</span></p></div>;
    $[35] = errorMessage;
    $[36] = lookupGrantId;
    $[37] = showLookupError;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== grant || $[40] !== isLookupFresh) {
    t19 = isLookupFresh && !!grant && <GroupCreateXtdhGrantRow grant={grant} isSelected={true} interactive={false} className="tw-mt-3" />;
    $[39] = grant;
    $[40] = isLookupFresh;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== showNonGrantedWarning) {
    t20 = showNonGrantedWarning && <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-300/30 tw-bg-amber-300/10 tw-p-3"><p className="tw-m-0 tw-text-xs tw-font-medium tw-text-amber-300">Selected grant status is not GRANTED. This filter is still allowed and will be submitted.</p></div>;
    $[42] = showNonGrantedWarning;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = beneficiaryGrantId ?? null;
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setIsModalOpen(false);
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== setBeneficiaryGrantId) {
    t23 = selectedGrant => {
      setBeneficiaryGrantId(selectedGrant.id);
      setLookupGrantId(selectedGrant.id);
      setIsModalOpen(false);
    };
    $[45] = setBeneficiaryGrantId;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== isModalOpen || $[48] !== t21 || $[49] !== t23) {
    t24 = <GroupCreateXtdhGrantModal isOpen={isModalOpen} selectedGrantId={t21} onClose={t22} onGrantSelect={t23} />;
    $[47] = isModalOpen;
    $[48] = t21;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t19 || $[55] !== t20 || $[56] !== t24) {
    t25 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-p-3 tw-shadow sm:tw-p-5">{t9}{t16}{t17}{t18}{t19}{t20}{t24}</div>;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  return t25;
}